import {
  getDocs,
  query,
  collection,
  orderBy,
  limit,
  deleteDoc,
  doc,
} from "firebase/firestore";
import React, { createContext, useState, useEffect, useContext } from "react";
import { db } from "../firebase";
import { QuotationFireStoreFields } from "../types/canvasTypes";

interface Props {
  children: React.ReactNode;
}

export const QuotationListContext = createContext<QuotationListContext>({
  getQuotations: () => Promise.resolve(),
  loading: false,
  quotations: [],
  deleteQuotation: () => {},
});

interface QuotationListContext {
  getQuotations: (forceRefresh?: boolean) => Promise<void>;
  loading: boolean;
  quotations: QuotationFireStoreFields[];
  deleteQuotation: (id: string, name: string, email: string) => void;
}

export const QuotationListContextProvider = ({ children }: Props) => {
  const [quotations, setQuotationList] = useState<QuotationFireStoreFields[]>(
    []
  );
  const [loading, setLoading] = useState(true);

  const getQuotations = async (
    forceRefresh: boolean = false
  ): Promise<void> => {
    if (!forceRefresh && !loading && quotations.length > 0) {
      return;
    }
    const data = await fetchQuotations();
    setQuotationList(data);
    return;
  };

  const deleteQuotation = async (id: string, name: string, email: string) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this quotation for " +
        name +
        " (" +
        email +
        ")?"
    );
    if (confirmDelete) {
      try {
        await deleteDoc(doc(db, "quotations", id));

        setQuotationList(quotations.filter((quotation) => quotation.id !== id));
      } catch (error) {
        console.error(error);
      }
    }
  };

  const fetchQuotations = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(
        query(collection(db, "quotations"), orderBy("date", "desc"), limit(50))
      );
      const data = querySnapshot.docs.map((doc) => {
        const docData = doc.data() as QuotationFireStoreFields;
        return {
          ...docData,
          id: doc.id,
        };
      });
      setLoading(false);
      return data;
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
    return [];
  };

  return (
    <QuotationListContext.Provider
      value={{ getQuotations, loading, quotations, deleteQuotation }}
    >
      {children}
    </QuotationListContext.Provider>
  );
};

export const useFetchQuotationContext = () => useContext(QuotationListContext);
